import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Heart, Compass, Lightbulb, Target, Sparkles, ChevronRight, User } from 'lucide-react';
import axios from 'axios';
import PremiumLock from '@/components/PremiumLock';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryIcons = {
  values: Heart,
  patterns: Compass,
  needs: Lightbulb,
  strengths: Target,
  dreams: Sparkles
};

const categoryColors = {
  values: 'bg-rose-100/50',
  patterns: 'bg-amber-100/50',
  needs: 'bg-sky-100/50',
  strengths: 'bg-emerald-100/50',
  dreams: 'bg-violet-100/50'
};

export default function SelfDiscoveryPage() {
  const navigate = useNavigate();
  const { hasAccess } = useAuth();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showInsight, setShowInsight] = useState(false);

  useEffect(() => {
    if (hasAccess) {
      fetchCategories();
    } else {
      setLoading(false);
    }
  }, [hasAccess]);

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-background">
        <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
            <Button variant="ghost" onClick={() => navigate('/category/thoughts')} className="rounded-full hover:bg-secondary/80">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Tillbaka
            </Button>
            <h1 className="font-heading text-xl font-light">Lär känna dig själv</h1>
            <div className="w-24" />
          </div>
        </header>
        <PremiumLock feature="Lär känna dig själv" />
      </div>
    );
  }

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/self-discovery`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Kunde inte hämta innehåll');
    } finally {
      setLoading(false);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < selectedCategory.questions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      setShowInsight(false);
    }
  };

  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setShowInsight(false);
    }
  };

  const currentQuestion = selectedCategory?.questions[currentQuestionIndex];

  return (
    <div className="min-h-screen bg-background" data-testid="self-discovery-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => selectedCategory ? setSelectedCategory(null) : navigate('/category/thoughts')}
            className="rounded-full hover:bg-secondary/80"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {selectedCategory ? 'Alla kategorier' : 'Tillbaka'}
          </Button>
          <h1 className="font-heading text-xl font-light">Lär känna dig själv</h1>
          <div className="w-24" />
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
          </div>
        ) : selectedCategory ? (
          /* Question view */
          <div className="animate-fade-in">
            {/* Category header */}
            <div className={`${categoryColors[selectedCategory.id]} rounded-2xl p-8 mb-8`}>
              <div className="flex items-center gap-3 mb-4">
                {(() => {
                  const Icon = categoryIcons[selectedCategory.id] || User;
                  return <Icon className="w-6 h-6 text-primary" />;
                })()}
                <span className="text-sm text-muted-foreground">{selectedCategory.title}</span>
              </div>
              <p className="text-muted-foreground">{selectedCategory.description}</p>
            </div>

            {/* Progress */}
            <div className="flex items-center gap-2 mb-6">
              {selectedCategory.questions.map((_, index) => (
                <div 
                  key={index}
                  className={`h-2 flex-1 rounded-full transition-colors ${
                    index <= currentQuestionIndex ? 'bg-primary' : 'bg-secondary'
                  }`}
                />
              ))}
            </div>

            {/* Current question */}
            <div className="bg-card rounded-2xl p-8 mb-6 space-y-6">
              <p className="text-sm text-muted-foreground">
                Fråga {currentQuestionIndex + 1} av {selectedCategory.questions.length}
              </p>
              
              <h2 className="font-heading text-2xl leading-relaxed">
                {currentQuestion.question}
              </h2>

              {/* Follow-up question */}
              <div className="bg-secondary/30 rounded-xl p-5">
                <p className="text-sm text-muted-foreground mb-2">Fördjupning</p>
                <p className="text-foreground">{currentQuestion.follow_up}</p>
              </div>

              {/* Insight (toggle) */}
              <div>
                <Button
                  variant="outline"
                  onClick={() => setShowInsight(!showInsight)}
                  className="rounded-full w-full justify-center"
                  data-testid="toggle-insight-btn"
                >
                  <Lightbulb className="w-4 h-4 mr-2" />
                  {showInsight ? 'Dölj insikt' : 'Visa insikt'}
                </Button>
                
                {showInsight && (
                  <div className="mt-4 p-5 bg-primary/10 rounded-xl animate-fade-in">
                    <p className="text-sm text-primary font-medium mb-2">Reflektion</p>
                    <p className="text-foreground italic">{currentQuestion.insight}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
                className="rounded-full"
              >
                Föregående
              </Button>
              
              {currentQuestionIndex < selectedCategory.questions.length - 1 ? (
                <Button
                  onClick={handleNextQuestion}
                  className="rounded-full"
                >
                  Nästa fråga
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={() => setSelectedCategory(null)}
                  className="rounded-full bg-primary"
                  data-testid="finish-category-btn"
                >
                  Avsluta
                </Button>
              )}
            </div>
          </div>
        ) : (
          /* Category list */
          <>
            <div className="text-center mb-10 space-y-3 animate-fade-in">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-7 h-7 text-primary" />
                </div>
              </div>
              <h2 className="font-heading text-3xl font-light">Lär känna dig själv</h2>
              <p className="text-muted-foreground max-w-lg mx-auto">
                Utforska din inre värld genom frågor som öppnar upp för djupare självinsikt.
              </p>
            </div>

            <div className="space-y-4">
              {categories.map((category, index) => {
                const Icon = categoryIcons[category.id] || User;
                return (
                  <button
                    key={category.id}
                    onClick={() => { setSelectedCategory(category); setCurrentQuestionIndex(0); setShowInsight(false); }}
                    className={`w-full ${categoryColors[category.id]} p-6 rounded-2xl text-left hover:shadow-soft transition-shadow duration-300 opacity-0 animate-slide-up group flex items-center justify-between`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                    data-testid={`category-card-${category.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full bg-card flex items-center justify-center group-hover:-translate-y-1 transition-transform duration-300">
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-heading text-xl font-normal">{category.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">{category.description}</p>
                        <p className="text-xs text-muted-foreground mt-2">
                          {category.questions.length} frågor
                        </p>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                );
              })}
            </div>

            {/* Gentle reminder */}
            <div className="mt-12 text-center">
              <p className="text-sm text-muted-foreground font-light italic">
                "Att lära känna sig själv är livets viktigaste resa."
              </p>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
